import { create } from 'zustand';

/**
 * 客户端航迹缓存。
 *
 * 平台按设计**不存储**任何历史航迹：航线与轨迹只来自无人机自身的实时接口。
 * 因此这里只在页面打开期间累积一条「面包屑」，用于总览地图与单机地图上的蓝色尾迹，
 * 刷新即清空，也不会写回后端。
 *
 * 尾迹的语义是「无人机移动过」，不是「收到过遥测」：
 * 只有位移超过 `MIN_MOVE_M` 才落点，所以悬停不会留下任何痕迹，
 * 停稳后尾迹也自然停止生长（而不是继续糊成一团）。
 */
const MAX_POINTS = 600;
/** 位移超过该距离才记点，悬停时不会产生无意义的数据 */
const MIN_MOVE_M = 4;

const trails = new Map<string, [number, number][]>();

interface TrailState {
  version: number;
}

export const useTrailStore = create<TrailState>(() => ({ version: 0 }));

let pendingBump = false;
let lastBumpAt = 0;
/** 地图只需要 ~4Hz 的航迹更新，没必要每帧都重建 GeoJSON */
const BUMP_INTERVAL_MS = 250;

function bumpVersion(): void {
  const now = Date.now();
  if (now - lastBumpAt >= BUMP_INTERVAL_MS) {
    lastBumpAt = now;
    useTrailStore.setState((state) => ({ version: state.version + 1 }));
    return;
  }
  if (pendingBump) return;
  pendingBump = true;
  const delay = BUMP_INTERVAL_MS - (now - lastBumpAt);
  window.setTimeout(() => {
    pendingBump = false;
    lastBumpAt = Date.now();
    useTrailStore.setState((state) => ({ version: state.version + 1 }));
  }, delay);
}

function distanceM(a: [number, number], b: [number, number]): number {
  const latScale = 111_320;
  const lngScale = latScale * Math.cos((((a[1] + b[1]) / 2) * Math.PI) / 180);
  const dLat = (b[1] - a[1]) * latScale;
  const dLng = (b[0] - a[0]) * lngScale;
  return Math.sqrt(dLat * dLat + dLng * dLng);
}

export function recordTrailPoint(droneId: string, lng: number, lat: number): void {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
  const point: [number, number] = [lng, lat];
  let trail = trails.get(droneId);
  if (!trail) {
    trail = [];
    trails.set(droneId, trail);
  }
  const last = trail[trail.length - 1];
  if (last && distanceM(last, point) < MIN_MOVE_M) return;

  trail.push(point);
  if (trail.length > MAX_POINTS) trail.splice(0, trail.length - MAX_POINTS);
  bumpVersion();
}

export function getTrail(droneId: string): readonly [number, number][] {
  return trails.get(droneId) ?? [];
}

export function clearTrail(droneId?: string): void {
  if (droneId === undefined) trails.clear();
  else trails.delete(droneId);
  useTrailStore.setState((state) => ({ version: state.version + 1 }));
}
